import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { appNav } from "../../routes/appNavigation";
import type { DashboardData } from "../../types";
import "./EventSetupChecklist.css";

type Props = {
  eventId: string;
  data: DashboardData;
  budgetCount: number;
  scheduleCount: number;
};

const EXPANDED_KEY_PREFIX = "zoniq-setup-checklist-";

/** Default: open while there is work left, collapsed once everything is done. */
function readExpanded(eventId: string, complete: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(`${EXPANDED_KEY_PREFIX}${eventId}`);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    // Private mode / storage disabled — fall through to the default.
  }
  return !complete;
}

function writeExpanded(eventId: string, expanded: boolean): void {
  try {
    window.localStorage.setItem(`${EXPANDED_KEY_PREFIX}${eventId}`, expanded ? "1" : "0");
  } catch {
    // Persistence is a convenience; never let it break the toggle.
  }
}

type ChecklistItem = {
  id: string;
  label: string;
  done: boolean;
  to: string;
};

export const EventSetupChecklist: React.FC<Props> = ({ eventId, data, budgetCount, scheduleCount }) => {
  const event = data.event;
  const tiers = data.event.ticket_tiers ?? [];
  const hasTiers = tiers.length > 0;
  const hasSold = tiers.some((t) => (t.sold ?? 0) > 0);
  const hasFinances =
    Boolean(event.finances) &&
    (Number(event.finances?.production_cost ?? 0) > 0 ||
      Number(event.finances?.artist_fee ?? 0) > 0 ||
      Number(event.finances?.marketing_cost ?? 0) > 0);
  const status = event.status.toLowerCase();
  const isLive = ["on_sale", "active", "live"].includes(status);

  const items = useMemo<ChecklistItem[]>(
    () => [
      {
        id: "tiers",
        label: "Ticket tiers configured",
        done: hasTiers,
        to: appNav.eventTickets(eventId),
      },
      {
        id: "budget",
        label: "Budget lines added",
        done: budgetCount > 0 || hasFinances,
        to: appNav.eventBudget(eventId),
      },
      {
        id: "pnl",
        label: "P&L reviewed",
        done: hasFinances,
        to: appNav.eventPnl(eventId),
      },
      {
        id: "schedule",
        label: "Run of show scheduled",
        done: scheduleCount > 0,
        to: appNav.eventRunOfShow(eventId),
      },
      {
        id: "live",
        label: "Status set for sale / live",
        done: isLive,
        to: appNav.eventOverview(eventId),
      },
      {
        id: "sales",
        label: "First tickets logged",
        done: hasSold,
        to: appNav.eventLive(eventId),
      },
    ],
    [budgetCount, eventId, hasFinances, hasSold, hasTiers, isLive, scheduleCount],
  );

  const doneCount = items.filter((i) => i.done).length;
  const complete = doneCount === items.length;

  // Sprint 14: the block collapses instead of vanishing. It previously
  // returned null once every step was done, so a fully set-up event lost the
  // section entirely and there was no way to review the steps again.
  const [expanded, setExpanded] = useState(() => readExpanded(eventId, complete));

  useEffect(() => {
    // A newly-incomplete event (a tier was deleted, say) should surface
    // itself again rather than staying collapsed from a previous visit.
    if (!complete && readExpanded(eventId, complete)) setExpanded(true);
  }, [complete, eventId]);

  const toggle = useCallback(() => {
    setExpanded((current) => {
      writeExpanded(eventId, !current);
      return !current;
    });
  }, [eventId]);

  return (
    <section
      className={`event-setup-checklist${expanded ? "" : " is-collapsed"}`}
      aria-label="Event setup checklist"
    >
      <button
        type="button"
        className="event-setup-checklist__head"
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={`event-setup-checklist-${eventId}`}
      >
        <div>
          <h2 className="event-setup-checklist__title">
            {complete ? "Setup complete" : "Finish setting up this show"}
          </h2>
          <p className="event-setup-checklist__lede">
            {doneCount} of {items.length} setup steps complete
          </p>
        </div>
        <div className="event-setup-checklist__progress" aria-hidden>
          <div className="event-setup-checklist__progress-fill" style={{ width: `${(doneCount / items.length) * 100}%` }} />
        </div>
        <span className="event-setup-checklist__chevron" aria-hidden>
          {expanded ? "▾" : "▸"}
        </span>
      </button>
      <ul
        id={`event-setup-checklist-${eventId}`}
        className="event-setup-checklist__list"
        hidden={!expanded}
      >
        {items.map((item) => (
          <li key={item.id} className={`event-setup-checklist__item${item.done ? " is-done" : ""}`}>
            <span className="event-setup-checklist__mark" aria-hidden>
              {item.done ? "✓" : "○"}
            </span>
            <span className="event-setup-checklist__label">{item.label}</span>
            {!item.done ? (
              <Link to={item.to} className="event-setup-checklist__link">
                Complete →
              </Link>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
};
